import type { GenieIconName } from "@/components/ui/genie-icons/genie-icons";

export interface TutorPromptGroup {
  key: string;
  title: string;
  description?: string;
  genieIcon: GenieIconName;
  prompts: string[];
}

/**
 * Suggested prompts shown on the Learn → Ask the Tutor tab.
 * Grouped by area of the Help Genie app so users can scan to what they need.
 *
 * Each prompt is the literal text that will be sent to the Tutor when clicked.
 * Write them as the user would type them — natural, specific, action-oriented.
 *
 * Section icons must come from the brand icon set in `/public/icons/`. Lucide
 * icons are not allowed for section headers (see guidelines/design.md §3).
 */
export const TUTOR_PROMPT_CATALOG: TutorPromptGroup[] = [
  {
    key: "getting-started",
    title: "Getting started",
    description: "Setting up your first Genie and finding your way around.",
    genieIcon: "genie-lamp",
    prompts: [
      "How do I create my first Genie?",
      "What use case should I pick when setting up a Genie?",
      "Can I change a Genie's name, voice, or use case after I create it?",
      "Can I paste in an existing system prompt during setup?",
      "Where do I find a Genie I've already created?",
    ],
  },
  {
    key: "knowledge",
    title: "Knowledge & content",
    description: "Feeding your Genie documents, web pages, and FAQs.",
    genieIcon: "book-open",
    prompts: [
      "What should I add to my Genie's knowledge base?",
      "What file types and sources are supported?",
      "How do I attach a document to a specific Genie?",
      "My Genie is giving wrong or outdated answers — how do I fix that?",
      "How do I update or replace a document I've already uploaded?",
      "What's the best way to structure a knowledge document?",
    ],
  },
  {
    key: "voices-branding",
    title: "Voices & branding",
    description: "Picking how your Genie sounds and looks.",
    genieIcon: "microphone",
    prompts: [
      "How do I find the right voice for my Genie?",
      "Can I create or clone a custom voice?",
      "My Genie mispronounces our product name — how do I fix it?",
      "How do I brand my Genie with my logo and colours?",
      "What's the difference between account branding and per-Genie branding?",
    ],
  },
  {
    key: "publishing",
    title: "Publishing & channels",
    description: "Sharing your Genie via Portals, embeds, phone, and more.",
    genieIcon: "share",
    prompts: [
      "Walk me through publishing a Portal.",
      "How do I embed my Genie's widget on my website?",
      "How do I give my Genie a phone number?",
      "Can I bring my own phone number to Help Genie?",
      "How do I make a Portal password-protected or private?",
      "Can one Portal host more than one Genie?",
    ],
  },
  {
    key: "conversations",
    title: "Conversations & insights",
    description: "Reviewing what your Genie has done and what to learn from it.",
    genieIcon: "chat-check",
    prompts: [
      "Where do I see all the conversations my Genie has had?",
      "How do I get the most out of the Insights page?",
      "Can I export conversations or insights to a spreadsheet?",
      "What should I look for when I review conversations each week?",
      "How do I know if my Genie is actually performing well?",
    ],
  },
  {
    key: "leads-integrations",
    title: "Leads & integrations",
    description: "Capturing leads and connecting Help Genie to other tools.",
    genieIcon: "users",
    prompts: [
      "How does lead capture work?",
      "Where do my captured leads end up?",
      "How do I send leads to my CRM or email inbox?",
      "How do I connect Slack or Microsoft Teams to my Genie?",
      "Can my Genie send notifications via email or webhook?",
      "What's the difference between Connections and phone numbers?",
    ],
  },
  {
    key: "troubleshooting",
    title: "Troubleshooting",
    description: "When something doesn't behave the way you expected.",
    genieIcon: "eye",
    prompts: [
      "My Genie isn't answering correctly — what should I check first?",
      "My Genie keeps repeating itself or sounding robotic.",
      "Why is my Genie slow to respond on voice calls?",
      "The voice sounds wrong — how do I tune it?",
      "I uploaded a document but my Genie doesn't seem to use it.",
      "How do I report a problem to the Help Genie team?",
    ],
  },
];
